/**
 * The curated global library — the shared shelf, from the side that writes it.
 *
 * The one listing in the product that shows **archived rows too**. Browse filters them out
 * (`archivedAt == null`) because an archived global is off the shelf; here they have to be
 * visible, because restoring one is the only way back and the take-down has to be reversible
 * in practice rather than in principle.
 *
 * Each card says whether the piece is **drawn**, because that is what decides whether a
 * parent can use it at all: Browse's *Use as is* is disabled until a global has a ready base
 * reference, and drawing one is this app's job (PRD 06, the admin-pre-render lean).
 */

import { Button, Chip, EmptyState, PieceCard, SurfaceHeader } from '@atlas-nook/design-system';
import { DEFAULT_THEME_ID } from '@atlas-nook/brand';
import { variantInstanceId } from '@atlas-nook/story';
import type { Primitive, PrimitiveKind } from '@atlas-nook/story';
import { useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router';

import { useGlobals, useVariantInstance } from '../data/queries.js';
import { ADMIN_STRINGS as S } from '../strings.js';
import { PRIMITIVE_KIND } from '../catalog/options.js';
import { NewGlobalDialog } from './NewGlobalDialog.js';
import { RenderImage } from './RenderImage.js';

const KINDS: readonly PrimitiveKind[] = ['character', 'setting', 'prop', 'beat'];

function isArchived(piece: Primitive): boolean {
  return piece.archivedAt !== null && piece.archivedAt !== undefined;
}

export function GlobalsList(): JSX.Element {
  const navigate = useNavigate();
  const { data: shelf, loading } = useGlobals();
  const [kinds, setKinds] = useState<Set<PrimitiveKind>>(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);

  const archived = shelf.filter(isArchived).length;
  const shown = shelf.filter(
    (piece) =>
      (kinds.size === 0 || kinds.has(piece.kind)) && (showArchived ? true : !isArchived(piece)),
  );

  function toggleKind(kind: PrimitiveKind): void {
    setKinds((was) => {
      const next = new Set(was);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }

  return (
    <>
      <SurfaceHeader
        kicker={S.globals.kicker}
        title={S.globals.title}
        meta={S.globals.meta(shelf.length - archived, archived)}
        actions={
          <Button tone="primary" onClick={() => setCreating(true)}>
            {S.globals.newGlobal}
          </Button>
        }
      />

      <div className="chiprow" role="group" aria-label={S.globals.filterLabel}>
        {KINDS.map((kind) => (
          <Chip
            key={kind}
            pressed={kinds.has(kind)}
            count={shelf.filter((piece) => piece.kind === kind).length}
            onClick={() => toggleKind(kind)}
          >
            {PRIMITIVE_KIND[kind]}
          </Chip>
        ))}
        <Chip
          pressed={showArchived}
          count={archived}
          onClick={() => setShowArchived((was) => !was)}
        >
          {S.globals.showArchived}
        </Chip>
      </div>

      {!loading && shown.length === 0 ? (
        <EmptyState title={S.globals.empty} />
      ) : (
        <div className="pgrid">
          {shown.map((piece) => (
            <GlobalCard key={piece.id} piece={piece} />
          ))}
        </div>
      )}

      <NewGlobalDialog
        open={creating}
        taken={new Set(shelf.map((piece) => piece.id))}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          void navigate(`/globals/${id}`);
        }}
      />
    </>
  );
}

/**
 * One curated piece.
 *
 * The base reference is read **by id**, not by a query: a global render carries no
 * `requestedBy` and the rules therefore never let it be listed. That is the same read Browse
 * makes, from the other side of the same rule.
 */
function GlobalCard({ piece }: { piece: Primitive }): JSX.Element {
  const navigate = useNavigate();
  const { data: base } = useVariantInstance(
    variantInstanceId(piece.id, piece.version, {}, DEFAULT_THEME_ID),
  );

  const drawn = base?.status === 'ready' && base.assetPath !== undefined;
  const archived = isArchived(piece);

  return (
    <PieceCard
      kind={`${PRIMITIVE_KIND[piece.kind]}${archived ? ` · ${S.globals.archivedTag}` : ''}`}
      name={piece.name.en}
      note={piece.identityNotes?.en ?? piece.description?.en}
      status={drawn ? 'ready' : base === null ? 'none' : 'generating'}
      statusLabel={drawn ? S.globals.drawn : S.globals.notDrawn}
      thumb={drawn ? 'filled' : 'empty'}
      thumbnail={<RenderImage path={base?.assetPath} alt={piece.name.en} />}
      thumbLabel={S.globals.notDrawn}
      archived={archived}
      action={
        <Button tone="quiet" size="sm" onClick={() => void navigate(`/globals/${piece.id}`)}>
          {S.catalog.open}
        </Button>
      }
    />
  );
}
